import { useState, useEffect } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'

import { resizeAvatar_250 } from 'commons/resizeImage'
import { FormattedMessage } from 'react-intl'

export default function PhotoGrid({
  photos = [],
  onAddMore,
  maxVisible,
  className,
}) {
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024,
  )

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Calculate max visible based on screen size
  const calculateMaxVisible = () => {
    if (maxVisible) return maxVisible
    if (windowWidth < 640) return 3 // 1 column × 3 rows
    if (windowWidth < 768) return 6 // 2 columns × 3 rows
    if (windowWidth < 1024) return 9 // 3 columns × 3 rows
    return 12 // 4 columns × 3 rows
  }

  const maxVisiblePhotos = calculateMaxVisible()
  const visiblePhotos = photos.slice(0, maxVisiblePhotos)
  const remainingCount = Math.max(0, photos.length - maxVisiblePhotos)
  const showMoreOverlay = remainingCount > 0

  // Single photo layout
  if (photos.length === 1) {
    const photo = photos[0]
    return (
      <div className="flex justify-center">
        <div className="relative w-[75px] h-[75px] rounded-lg overflow-hidden">
          <img
            src={resizeAvatar_250(photo.url || photo.path)}
            alt="Gallery photo"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = '/placeholder.svg?height=80&width=80&text=Error'
            }}
          />
        </div>
      </div>
    )
  }

  // Grid layout for multiple photos
  return (
    <div className={clsx('flex justify-center', className)}>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {visiblePhotos.map((photo, index) => {
          const isLastVisible = index === maxVisiblePhotos - 1
          const showOverlay = showMoreOverlay && isLastVisible

          return (
            <div
              key={photo.id || index}
              className="relative w-[65px] h-[65px] rounded-lg overflow-hidden"
            >
              <img
                src={resizeAvatar_250(photo.url)}
                alt={`Photo Gallery ${index + 1}`}
                className="w-full h-full object-cover"
              />

              {showOverlay && (
                <div
                  className="absolute inset-0 bg-black/60 flex items-center justify-center cursor-pointer"
                  onClick={() => onAddMore()}
                >
                  <div className="text-white text-center text-xs font-medium">
                    <div className="uppercase">
                      <FormattedMessage
                        id="seeMore.label"
                        values={{
                          b: (text) => <div>{text}</div>,
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

PhotoGrid.propTypes = {
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      url: PropTypes.string,
      path: PropTypes.string,
    }),
  ),
  onAddMore: PropTypes.func,
  maxVisible: PropTypes.number,
  className: PropTypes.string,
}
